import { Component } from '@theme/component';
import { prefersReducedMotion } from '@theme/utilities';

/**
 * Horizontal gallery rail. The track scrolls natively (touch, trackpad, keyboard
 * focus); the dashed arrows step one slide at a time, and an optional list of
 * links follows the visible slide and brings its slide forward on hover or focus.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} track - Scrolling container
 * @property {HTMLElement[]} [slides] - Slides inside the track
 * @property {HTMLButtonElement} [previous] - Previous arrow
 * @property {HTMLButtonElement} [next] - Next arrow
 * @property {HTMLElement[]} [items] - List links paired with the slides by index
 *
 * @extends {Component<Refs>}
 */
class FifiCarouselComponent extends Component {
  requiredRefs = ['track'];

  /** @type {AbortController | null} */
  #controller = null;

  #frame = 0;

  /** @type {number | null} Slide chosen from the list, kept current while visible */
  #pinned = null;

  connectedCallback() {
    super.connectedCallback();
    this.#listen();
  }

  updatedCallback() {
    super.updatedCallback();
    this.#listen();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#controller?.abort();
    cancelAnimationFrame(this.#frame);
  }

  /** Index of the slide nearest the start edge of the track. */
  get currentIndex() {
    const slides = this.refs.slides ?? [];
    const trackRect = this.refs.track.getBoundingClientRect();
    const rtl = this.#isRtl;
    let current = 0;
    let nearest = Infinity;

    for (const [index, slide] of slides.entries()) {
      const rect = slide.getBoundingClientRect();
      const distance = Math.abs(rtl ? trackRect.right - rect.right : rect.left - trackRect.left);
      if (distance < nearest) {
        nearest = distance;
        current = index;
      }
    }

    return current;
  }

  /**
   * Scrolls the track so the slide at `index` starts at the track's start edge.
   * @param {number} index
   */
  scrollToSlide(index) {
    const slide = this.refs.slides?.[index];
    if (!slide) return;

    const trackRect = this.refs.track.getBoundingClientRect();
    const rect = slide.getBoundingClientRect();
    const left = this.#isRtl ? rect.right - trackRect.right : rect.left - trackRect.left;

    this.refs.track.scrollBy({ left, behavior: prefersReducedMotion() ? 'auto' : 'smooth' });
  }

  /** Handles the previous arrow. */
  previous() {
    this.#pinned = null;
    this.scrollToSlide(Math.max(0, this.currentIndex - 1));
  }

  /** Handles the next arrow. */
  next() {
    this.#pinned = null;
    const last = (this.refs.slides?.length ?? 1) - 1;
    this.scrollToSlide(Math.min(last, this.currentIndex + 1));
  }

  /**
   * Brings a list item's slide forward when the item is hovered or focused.
   * @param {Event} event
   */
  showItem(event) {
    const target = event.target;
    if (!(target instanceof HTMLElement)) return;

    const index = Number(target.dataset.index);
    if (!Number.isInteger(index)) return;

    this.#pinned = index;
    this.scrollToSlide(index);
    this.#queueUpdate();
  }

  get #isRtl() {
    return getComputedStyle(this).direction === 'rtl';
  }

  #listen() {
    this.#controller?.abort();
    this.#controller = new AbortController();
    const { signal } = this.#controller;

    this.refs.track.addEventListener('scroll', () => this.#queueUpdate(), { passive: true, signal });
    window.addEventListener('resize', () => this.#queueUpdate(), { passive: true, signal });

    this.#update();
  }

  #queueUpdate() {
    cancelAnimationFrame(this.#frame);
    this.#frame = requestAnimationFrame(() => this.#update());
  }

  #update() {
    const { track, previous, next, items } = this.refs;
    const max = track.scrollWidth - track.clientWidth;
    const position = Math.abs(track.scrollLeft);

    this.toggleAttribute('data-scrollable', max > 2);
    if (previous) previous.disabled = position <= 2;
    if (next) next.disabled = position >= max - 2;

    if (!items?.length) return;

    let current = position >= max - 2 && max > 2 ? this.#lastVisibleIndex() : this.currentIndex;
    if (this.#pinned !== null && this.#isVisible(this.#pinned)) {
      current = this.#pinned;
    } else {
      this.#pinned = null;
    }

    for (const [index, item] of items.entries()) {
      if (index === current) {
        item.setAttribute('aria-current', 'true');
      } else {
        item.removeAttribute('aria-current');
      }
    }
  }

  /** @param {number} index */
  #isVisible(index) {
    const slide = this.refs.slides?.[index];
    if (!slide) return false;

    const trackRect = this.refs.track.getBoundingClientRect();
    const rect = slide.getBoundingClientRect();
    return rect.left >= trackRect.left - 2 && rect.right <= trackRect.right + 2;
  }

  /** At the end of the track the last slide may never reach the start edge. */
  #lastVisibleIndex() {
    const slides = this.refs.slides ?? [];
    const trackRect = this.refs.track.getBoundingClientRect();
    let last = 0;

    for (const [index, slide] of slides.entries()) {
      const rect = slide.getBoundingClientRect();
      if (rect.left < trackRect.right && rect.right > trackRect.left) last = index;
    }

    return last;
  }
}

if (!customElements.get('fifi-carousel-component')) {
  customElements.define('fifi-carousel-component', FifiCarouselComponent);
}
